<template>
  <div class="project-list-page">
    <WelcomeBanner title="项目列表" subtitle="管理并查看项目概览">
      <div slot="right">
        <el-button type="success" icon="el-icon-plus" @click="showCreateDialog">创建项目</el-button>
      </div>
    </WelcomeBanner>

    <div class="filter-bar">
      <el-select v-model="filters.status" placeholder="项目状态" clearable style="width: 150px; margin-right: 10px;">
        <el-option label="全部" value="" />
        <el-option label="进行中" value="1" />
        <el-option label="已暂停" value="2" />
        <el-option label="已完成" value="3" />
      </el-select>
      <el-select v-model="filters.myProjects" placeholder="我的项目" clearable style="width: 150px; margin-right: 10px;">
        <el-option label="全部" value="" />
        <el-option label="是" value="1" />
      </el-select>
      <el-input v-model="keywords" placeholder="搜索项目名称" clearable style="width: 200px; margin-right: 10px;"></el-input>
      <el-button type="primary" @click="loadProjects">搜索</el-button>
    </div>

    <BaseCard>
      <div v-loading="loading" class="project-grid-container">
      <el-row :gutter="20" v-if="displayList.length" class="project-grid">
        <el-col :span="8" v-for="item in displayList" :key="item.projectId || 'create'">
          <!-- 创建新项目卡片 -->
          <el-card v-if="item.isCreateCard" class="project-card create-card" shadow="hover" @click.native="showCreateDialog">
            <div class="create-card-content">
              <i class="el-icon-plus create-icon"></i>
              <span class="create-text">创建新项目</span>
            </div>
          </el-card>
          <!-- 普通项目卡片 -->
          <el-card v-else class="project-card" shadow="hover">
            <div class="card-header">
              <div class="header-top">
                <h3 class="project-name" :title="item.projectName">{{ item.projectName }}</h3>
                <el-tag :type="getStatusType(item.status)" size="small">{{ getStatusText(item.status) }}</el-tag>
              </div>
            </div>
            <div class="card-body">
              <p class="project-desc" :title="item.projectDesc">{{ item.projectDesc || '暂无描述' }}</p>
              <div class="info-row">
                <span class="label">负责人：</span>
                <span class="value">{{ item.ownerName || '-' }}</span>
              </div>
              <div class="info-row">
                <span class="label">项目ID：</span>
                <span class="value project-id">{{ item.projectId }}</span>
              </div>
              <div class="stats-row">
                <div class="stat-item">
                  <div class="stat-value">{{ item.totalTasks }}</div>
                  <div class="stat-label">总任务</div>
                </div>
                <div class="stat-item">
                  <div class="stat-value">{{ item.completedTasks }}</div>
                  <div class="stat-label">已完成</div>
                </div>
                <div class="stat-item">
                  <div class="stat-value">{{ item.inProgressTasks || 0 }}</div>
                  <div class="stat-label">进行中</div>
                </div>
              </div>
              <div class="progress-bar" v-if="item.totalTasks > 0">
                <div class="progress-track">
                  <div class="progress-fill" :style="{ width: (item.completedTasks / item.totalTasks * 100) + '%' }"></div>
                </div>
                <span class="progress-text">{{ Math.round(item.completedTasks / item.totalTasks * 100) }}%</span>
              </div>
            </div>
            <div class="card-footer">
              <div class="time-info">
                <span class="time-item">
                  <i class="el-icon-date"></i>
                  创建：{{ formatTime(item.createTime) }}
                </span>
                <span class="time-item" v-if="item.updateTime">
                  <i class="el-icon-refresh"></i>
                  更新：{{ formatTime(item.updateTime) }}
                </span>
              </div>
              <el-button type="primary" size="small" @click="goDetail(item.projectId)">查看详情</el-button>
            </div>
          </el-card>
        </el-col>
      </el-row>
      <div v-if="!list.length && !loading" class="empty-text">暂无数据</div>
      </div>
    </BaseCard>
    
    <div class="pagination" v-if="pagination.total > 0">
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="pagination.currentPage"
        :page-sizes="[9, 18, 36]"
        :page-size="pagination.pageSize"
        :total="pagination.total"
        layout="total, sizes, prev, pager, next, jumper"
      ></el-pagination>
    </div>

    <!-- 创建项目弹窗 -->
    <el-dialog
      title="创建新项目"
      :visible.sync="createDialogVisible"
      width="500px"
      :close-on-click-modal="false"
    >
      <el-form
        ref="createForm"
        :model="createForm"
        :rules="createRules"
        label-width="100px"
        label-position="right"
      >
        <el-form-item label="项目名称" prop="name">
          <el-input
            v-model="createForm.name"
            placeholder="请输入项目名称"
            maxlength="50"
            show-word-limit
          ></el-input>
        </el-form-item>
        <el-form-item label="项目描述" prop="description">
          <el-input
            v-model="createForm.description"
            type="textarea"
            :rows="4"
            placeholder="请输入项目描述（可选）"
            maxlength="500"
            show-word-limit
          ></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="createDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="creating" @click="handleCreate">创建</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getProjectListPaged, createProject } from '../api/project'
import BaseCard from '@/components/BaseCard'
import WelcomeBanner from '@/components/WelcomeBanner'

export default {
  name: 'ProjectList',
  components: { BaseCard, WelcomeBanner },
  data() {
    return {
      loading: false,
      creating: false,
      list: [],
      keywords: '',
      filters: {
        status: '',
        myProjects: ''
      },
      pagination: {
        currentPage: 1,
        pageSize: 9,
        total: 0
      },
      createDialogVisible: false,
      createForm: {
        name: '',
        description: ''
      },
      createRules: {
        name: [
          { required: true, message: '请输入项目名称', trigger: 'blur' },
          { min: 2, max: 50, message: '项目名称长度应为2-50个字符', trigger: 'blur' }
        ],
        description: [
          { max: 500, message: '项目描述不能超过500个字符', trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    // 在项目列表末尾添加创建卡片
    displayList() {
      const list = [...this.list]
      // 添加创建新项目卡片
      list.push({
        projectId: 'create-new-project',
        isCreateCard: true
      })
      return list
    }
  },
  created() {
    this.loadProjects()
  },
  methods: {
    async loadProjects() {
      this.loading = true
      try {
        // 拦截器已返回 res.data
        const data = await getProjectListPaged({
          page: this.pagination.currentPage,
          page_size: this.pagination.pageSize,
          keyword: this.keywords
        })
        if (data) {
          this.list = (data.list || []).map(item => ({
            projectId: item.project_id || item.id,
            projectName: item.project_name || item.name,
            projectDesc: item.project_desc || item.description,
            status: item.status,
            isActive: item.is_active,
            ownerId: item.owner_id,
            ownerName: item.owner_name || item.ownerName,
            createTime: item.create_time,
            updateTime: item.update_time,
            totalTasks: item.total_tasks || 0,
            completedTasks: item.completed_tasks || 0,
            inProgressTasks: item.in_progress_tasks || item.inProgressTasks || 0
          }))
          this.pagination.total = data.total || 0
        }
      } catch (error) {
        console.error('获取项目列表失败:', error)
        this.$message.error(`获取数据失败: ${error.message || '未知错误'}`)
      } finally {
        this.loading = false
      }
    },
    formatTime(time) {
      if (!time) return '-'
      return new Date(time).toLocaleDateString('zh-CN')
    },
    goDetail(projectId) {
      this.$router.push(`/project/${projectId}`)
    },
    showCreateDialog() {
      this.createDialogVisible = true
      this.createForm = {
        name: '',
        description: ''
      }
      this.$nextTick(() => {
        this.$refs.createForm && this.$refs.createForm.clearValidate()
      })
    },
    async handleCreate() {
      try {
        await this.$refs.createForm.validate()
      } catch (error) {
        return
      }

      this.creating = true
      try {
        const result = await createProject({
          name: this.createForm.name.trim(),
          description: this.createForm.description.trim() || undefined
        })

        if (result && result.project_id) {
          this.$message.success('项目创建成功')
          this.createDialogVisible = false
          this.loadProjects()
        } else {
          this.$message.error('项目创建失败')
        }
      } catch (error) {
        console.error('创建项目失败:', error)
        this.$message.error(error.message || '项目创建失败')
      } finally {
        this.creating = false
      }
    },
    handleSizeChange(val) {
      this.pagination.pageSize = val
      this.loadProjects()
    },
    handleCurrentChange(val) {
      this.pagination.currentPage = val
      this.loadProjects()
    },
    getStatusType(status) {
      const map = {
        1: 'success',
        2: 'warning',
        3: 'info'
      }
      return map[status] || 'info'
    },
    getStatusText(status) {
      const map = {
        1: '进行中',
        2: '已暂停',
        3: '已完成'
      }
      return map[status] || '未知'
    }
  }
}
</script>

<style lang="scss" scoped>
.project-list-page {
  padding: 16px;
  min-height: calc(100vh - 100px);
  display: flex;
  flex-direction: column;
  background: linear-gradient(180deg, #f5f7fa 0%, #fff 100%);
}

.filter-bar {
  padding: 12px 16px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  background-color: #fff;
  border-radius: 10px;
  margin-bottom: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

.project-grid-container {
  flex: 1;
  background: #fff;
  border-radius: 10px;
  padding: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  margin-bottom: 16px;
}

.project-grid {
  width: 100%;
}

.project-card {
  margin-bottom: 16px;
  min-height: 300px;
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
  border: none;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  background: #fff;
  position: relative;

  &:hover {
    transform: translateY(-2px) scale(1.02);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.10);
    background: linear-gradient(135deg, #f5f7fa 0%, #e0e7ff 100%);
  }

  :deep(.el-card__body) {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 16px;
  }
}

/* 卡片头部 */
.card-header {
  margin-bottom: 12px;
  padding: 18px 18px 12px 18px;
  border-bottom: 1px solid #f0f2f5;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(135deg, #818cf8 0%, #a78bfa 100%);
  border-radius: 12px 12px 0 0;
  box-shadow: 0 2px 12px rgba(129, 140, 248, 0.10);
}

.header-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.project-name {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #fff;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  margin-right: 10px;
  letter-spacing: 0.5px;
}

/* 卡片内容 */
.card-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 18px;
}

.project-desc {
  margin: 0 0 6px 0;
  font-size: 13px;
  color: #909399;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  line-height: 1.5;
  min-height: 39px;
  padding-bottom: 8px;
  border-bottom: 1px dashed #e4e7ed;
}

.info-row {
  display: flex;
  font-size: 13px;

  .label {
    color: #909399;
    min-width: 60px;
  }

  .value {
    color: #606266;
    flex: 1;
  }

  .project-id {
    font-family: monospace;
    font-size: 12px;
    color: #909399;
  }
}

/* 统计行 */
.stats-row {
  display: flex;
  justify-content: space-around;
  padding: 12px 0;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(139, 92, 246, 0.05) 100%);
  border-radius: 8px;
  margin-top: auto;
}

.stat-item {
  text-align: center;
}

.stat-value {
  font-size: 20px;
  font-weight: 600;
  color: #6366f1;
  line-height: 1.2;
}

.stat-label {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
}

/* 进度条 */
.progress-bar {
  display: flex;
  align-items: center;
  gap: 10px;
}

.progress-track {
  flex: 1;
  height: 6px;
  background-color: #e4e7ed;
  border-radius: 3px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #10b981 0%, #6366f1 100%);
  border-radius: 3px;
  transition: width 0.3s ease;
}

.progress-text {
  font-size: 12px;
  color: #6366f1;
  font-weight: 500;
  min-width: 36px;
  text-align: right;
}

/* 卡片底部 */
.card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 12px;
  padding: 10px 18px;
  border-top: 1px solid #f0f2f5;
  background: #fafbfc;
  border-radius: 0 0 12px 12px;
}

.time-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.time-item {
  font-size: 12px;
  color: #909399;
  display: flex;
  align-items: center;
  gap: 4px;

  i {
    font-size: 12px;
  }
}

/* 创建新项目卡片 */
.create-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  background: linear-gradient(135deg, #e0e7ff 0%, #fff 100%);
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(129, 140, 248, 0.10);
  border: 2px dashed #a5b4fc;
  color: #6366f1;
  cursor: pointer;
  transition: all 0.3s;

  :deep(.el-card__body) {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .create-card-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
  }

  .create-icon {
    font-size: 48px;
    color: #c0c4cc;
    transition: all 0.3s;
  }

  &:hover {
    background: linear-gradient(135deg, #a5b4fc 0%, #fff 100%);
    border-color: #6366f1;
    color: #4f46e5;
    box-shadow: 0 4px 16px rgba(129, 140, 248, 0.15);
    transform: translateY(-2px) scale(1.03);
  }

  &:hover .create-icon {
    color: #6366f1;
    transform: scale(1.1);
  }

  .create-text {
    font-size: 14px;
    color: #909399;
    font-weight: 500;
    transition: all 0.3s;
  }

  &:hover .create-text {
    color: #6366f1;
  }
}

/* 空状态 */
.empty-text {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 0;
  color: #c0c4cc;
  font-size: 14px;
}

/* 分页 */
.pagination {
  display: flex;
  justify-content: center;
  padding: 18px 0 0 0;
}

/* 响应式优化 */
@media screen and (max-width: 1200px) {
  .project-grid-container {
    padding: 10px;
  }
  .project-card, .create-card {
    min-height: 220px;
  }
}
@media screen and (max-width: 768px) {
  .project-list-page {
    padding: 8px;
  }
  .project-grid-container {
    padding: 4px;
  }
  .project-card, .create-card {
    min-height: 160px;
    border-radius: 8px;
  }
  .card-header, .card-footer, .card-body {
    padding-left: 10px;
    padding-right: 10px;
  }
  .project-name {
    font-size: 15px;
  }
}
</style>
